import { useCallback, useMemo } from "react";
import { useClipboard } from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { showAlert } from "@root/store/slices/features";
import { TOAST_VARIANT } from "@root/constants";
import { useTranslation } from "react-i18next";
import { textToMarkdown } from "@root/utils/streaming";

type AnswerValue = string | string[] | null | undefined;

export function useCopyAnswer(answer: AnswerValue) {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");

  const copyMarkdown = useMemo(() => {
    if (!answer) {
      return "";
    }
    try {
      return textToMarkdown(answer);
    } catch (error) {
      return "";
    }
  }, [answer]);

  const copyPlainText = useMemo(() => {
    if (!copyMarkdown) {
      return "";
    }
    const div = document.createElement("div");
    div.innerHTML = copyMarkdown;
    return div.innerText || "";
  }, [copyMarkdown]);

  const { onCopy } = useClipboard(copyPlainText || "");

  const isCopyDisabled = !copyPlainText.trim();

  const handleCopy = useCallback(() => {
    if (isCopyDisabled) {
      return;
    }
    onCopy();
    dispatch(
      showAlert({
        type: TOAST_VARIANT.COPY_SUCCESS,
        message: t("msg.copySuccess"),
      })
    );
  }, [dispatch, isCopyDisabled, onCopy, t]);

  return {
    handleCopy,
    isCopyDisabled,
  };
}
